import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  SwipeableDrawer,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

const menuItems = [
  {
    label: 'Quick Post',
    description: 'Share text or an image',
    icon: <EditNoteIcon />,
    action: 'quick-post',
  },
  {
    label: 'Log Workout',
    description: 'Record your exercise',
    icon: <FitnessCenterIcon />,
    action: 'log-workout',
  },
  {
    label: 'Log Meal',
    description: 'Track your nutrition',
    icon: <RestaurantIcon />,
    action: 'log-meal',
  },
  {
    label: 'Progress Photo',
    description: 'Capture your transformation',
    icon: <CameraAltIcon />,
    action: 'progress-photo',
  },
];

/**
 * Create menu component.
 * Renders as a dropdown on desktop or a bottom sheet on mobile.
 * Now opens dedicated creation dialogs for each post type instead of routing.
 *
 * @param {Object} props
 * @param {HTMLElement|null} props.anchorEl - Anchor element for dropdown positioning
 * @param {boolean} props.open - Whether the menu is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onQuickPost - Handler for Quick Post action
 * @param {Function} props.onLogWorkout - Handler for Log Workout action
 * @param {Function} props.onLogMeal - Handler for Log Meal action
 * @param {Function} props.onProgressPhoto - Handler for Progress Photo action
 * @param {'dropdown'|'bottom-sheet'} props.variant - Display variant
 */
export const CreateMenu = ({
  anchorEl,
  open,
  onClose,
  onQuickPost,
  onLogWorkout,
  onLogMeal,
  onProgressPhoto,
  variant = 'dropdown',
}) => {
  const handleItemClick = (item) => {
    onClose();
    switch (item.action) {
      case 'quick-post':
        if (onQuickPost) onQuickPost();
        break;
      case 'log-workout':
        if (onLogWorkout) onLogWorkout();
        break;
      case 'log-meal':
        if (onLogMeal) onLogMeal();
        break;
      case 'progress-photo':
        if (onProgressPhoto) onProgressPhoto();
        break;
      default:
        break;
    }
  };

  if (variant === 'bottom-sheet') {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: 'env(safe-area-inset-bottom, 0px)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drag handle */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'text.disabled',
              mx: 'auto',
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ px: 1, mb: 1, fontWeight: 700 }}>
            Create
          </Typography>
          {menuItems.map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => handleItemClick(item)}
              sx={{
                py: 1.5,
                px: 1,
                borderRadius: 2,
                mb: 0.5,
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'primary.main',
                  minWidth: 44,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </MenuItem>
          ))}
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 4,
        sx: {
          overflow: 'visible',
          minWidth: 240,
          p: 0.5,
          borderRadius: 3,
        },
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
    >
      <Typography
        variant="overline"
        sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}
      >
        Create
      </Typography>
      {menuItems.map((item) => (
        <MenuItem
          key={item.label}
          onClick={() => handleItemClick(item)}
          sx={{
            py: 1.5,
            borderRadius: 1.5,
            mx: 0.5,
            mb: 0.5,
          }}
        >
          <ListItemIcon
            sx={{
              color: 'primary.main',
              minWidth: 40,
            }}
          >
            {item.icon}
          </ListItemIcon>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.description}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
};
